import { ReviewScreen } from '@/components/admin/sessions/review/review-screen';
import { TrashedBanner } from '@/components/admin/sessions/walk/trashed-banner';
import { useAdminSession, useRejectOn401 } from '@/lib/admin-session';
import { ApiError, fetchPersonaVersion } from '@/lib/api';
import { SESSION_DETAIL_KEY, fetchSessionDetail, restoreSession } from '@/lib/session-api';
import { REVIEW_KEY, fetchSessionReview } from '@/lib/session-review-api';
import { Spinner } from '@pms-digital/design-system';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';

/**
 * `/admin/sessions/<id>/relecture` - the REVIEW of a session (relecture.md §3.2,
 * issue #187, « mode Patrick »).
 *
 * ## The `_` of `$sessionId_`
 *
 * The page takes the screen alone, as a SISTER of the parcours and of the
 * conversation - the same opt-out of nesting as `sessions.$sessionId_.protocole.tsx`
 * and for the same reason: TanStack's flat routing would otherwise draw it inside
 * `sessions.$sessionId.tsx`.
 *
 * ## Four reads
 *
 * The session - the conversation, under the key the parcours and the conversation
 * share, so a page opened from either re-reads nothing -; the review; and, when
 * the session froze a persona, the version of it that was played, which is the only
 * place the interviewer's first name is written (the detail carries the persona's
 * NAME, and a persona with an anonymous presentation speaks as « IA »).
 *
 * The review is read again when the page gets the focus back (§4.4): a colleague
 * may have written meanwhile, and there is no channel in real time. A persona
 * that cannot be read is no reason to hide the page - the interviewer then speaks
 * as « IA », the closed answer.
 *
 * ## A session in the trash opens, and reads
 *
 * Under the banner of the trash, with its « Restaurer ». Restoring reads the
 * review again: it is writable once more, exactly as it was (§16).
 */

export const Route = createFileRoute('/admin/sessions/$sessionId_/relecture')({
  component: ReviewPage,
});

function ReviewPage() {
  const { email } = useAdminSession();
  const { sessionId } = Route.useParams();
  const client = useQueryClient();

  const session = useQuery({
    // The key the parcours and the conversation read under: one reading, three views.
    queryKey: SESSION_DETAIL_KEY(email, sessionId),
    queryFn: () => fetchSessionDetail(sessionId),
    retry: false,
  });
  const review = useQuery({
    queryKey: REVIEW_KEY(email, sessionId),
    queryFn: () => fetchSessionReview(sessionId),
    retry: false,
    // Stale at once, and read again at the focus: the review is a shared notebook.
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  const played = session.data?.cascade.persona ?? null;
  const persona = useQuery({
    queryKey: ['persona-version', played?.id ?? '', played?.versionId ?? ''],
    queryFn: () => fetchPersonaVersion(played?.id ?? '', played?.versionId ?? ''),
    enabled: played !== null,
    retry: false,
    staleTime: 5 * 60_000,
  });

  const restore = useMutation({
    mutationFn: () => restoreSession(sessionId),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: SESSION_DETAIL_KEY(email, sessionId) });
      void client.invalidateQueries({ queryKey: REVIEW_KEY(email, sessionId) });
      void client.invalidateQueries({ queryKey: ['sessions', email] });
    },
  });

  const rejectedSession = useRejectOn401(session.error);
  const rejectedReview = useRejectOn401(review.error);
  const missing = session.error instanceof ApiError && session.error.status === 404;

  if (session.isPending || review.isPending || (played !== null && persona.isPending)) {
    return (
      <div className="flex justify-center py-12">
        <Spinner label="Chargement de la relecture" />
      </div>
    );
  }

  if (missing) {
    return (
      <p className="text-gray-700">
        Cette session n’existe pas ou a été effacée.{' '}
        <Link to="/admin/sessions" className="text-bleu-nuit underline underline-offset-2">
          Revenir aux sessions
        </Link>
        .
      </p>
    );
  }

  if (session.data === undefined || review.data === undefined) {
    return rejectedSession || rejectedReview ? null : (
      <p role="alert" className="text-rouge text-small">
        Cette relecture n’a pas pu être lue. Rechargez la page.
      </p>
    );
  }

  const detail = session.data;
  return (
    <ReviewScreen
      detail={detail}
      review={review.data}
      presentation={persona.data?.content.presentation ?? null}
      email={email}
      banner={
        detail.session.deletedAt === null ? null : (
          <TrashedBanner
            deletedAt={detail.session.deletedAt}
            onRestore={() => restore.mutate()}
            busy={restore.isPending}
          />
        )
      }
    />
  );
}
